/**
 * Experiment variant parser for TRACI session IDs.
 *
 * Session ID format:
 *   Old:        session-{timestamp}-{uuid}
 *   POC:        session-{variant_code}-{timestamp}-{uuid}
 *   Versioned:  session-v{N}{variant_code}-{timestamp}-{uuid}
 *
 * Variant code example: "A1B2" means Feature A → variant 1, Feature B → variant 2.
 */

const VARIANT_PATTERN = /([a-zA-Z])(\d+)/g;
const VERSION_PATTERN = /^v(\d+)/;

/**
 * Parse experiment variant assignments from a session ID.
 *
 * @param sessionId - Full session ID string
 * @returns Object mapping feature letter to variant number, or empty object for old-format IDs
 *
 * @example
 * parseExperimentCode("session-v3A1B2-1703001234567-abcdef") // { A: 1, B: 2 }
 * parseExperimentCode("session-A1B2-1703001234567-abcdef")    // { A: 1, B: 2 }
 * parseExperimentCode("session-1703001234567-abcdef")          // {}
 */
export function parseExperimentCode(sessionId: string): Record<string, number> {
  if (!sessionId?.startsWith('session-')) return {};

  const parts = sessionId.split('-');
  if (parts.length < 3) return {};

  let candidate = parts[1];

  // Old format: second segment is all digits (timestamp)
  if (/^\d+$/.test(candidate)) return {};

  // Strip version prefix if present (e.g., "v3A1B2" → "A1B2")
  const versionMatch = candidate.match(VERSION_PATTERN);
  if (versionMatch) {
    candidate = candidate.slice(versionMatch[0].length);
    if (!candidate) return {};
  }

  const assignments: Record<string, number> = {};
  let reconstructed = '';

  for (const match of candidate.matchAll(VARIANT_PATTERN)) {
    assignments[match[1].toUpperCase()] = parseInt(match[2], 10);
    reconstructed += match[0];
  }

  // Verify matches cover the full string
  if (reconstructed !== candidate) return {};

  return assignments;
}

/**
 * Parse the experiment version number from a session ID.
 *
 * @param sessionId - Full session ID string
 * @returns Version number or null if no version is encoded
 */
export function parseExperimentVersion(sessionId: string): number | null {
  if (!sessionId?.startsWith('session-')) return null;

  const parts = sessionId.split('-');
  if (parts.length < 3) return null;

  const candidate = parts[1];
  const versionMatch = candidate.match(VERSION_PATTERN);
  if (versionMatch) {
    return parseInt(versionMatch[1], 10);
  }
  return null;
}
